import React from "react";
import { Upload, Type, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { MATERIALS } from "./constants";

const STICKER_SHAPES = [
  { key: "rounded", label: "Rounded", clip: "16px" },
  { key: "circle", label: "Circle", clip: "50%" },
  { key: "square", label: "Square", clip: "4px" },
  { key: "diecut", label: "Die-cut", clip: "28px" },
];

export function StudioLeftPanel({ mode, stickerShape, setStickerShape, material, setMaterial, plaqueShape, setPlaqueShape,
  applyBabyPlaquePreset, addText, onUpload, layers, selectedIds, selectLayer, removeLayer }) {
  return (
    <div className="space-y-6">
      {mode === "sticker" ? (
        <div className="bg-white p-5 rounded-lg border hairline">
          <div className="uppercase text-[10px] tracking-[0.3em] font-bold mb-3" style={{color: "var(--secondary)"}}>Sticker Shape</div>
          <div className="grid grid-cols-2 gap-3">
            {STICKER_SHAPES.map((s) => (
              <button key={s.key} data-testid={`shape-${s.key}`} onClick={() => setStickerShape(s.key)}
                className={`aspect-[4/3] rounded-md border-2 transition flex items-center justify-center ${stickerShape === s.key ? "border-[color:var(--accent-metal)] bg-[color:var(--accent-metal)]/5" : "border-transparent bg-[color:var(--muted)] hover:border-[color:var(--border)]"}`}>
                <div className="w-10 h-7 bg-white border border-dashed border-neutral-400" style={{borderRadius: s.clip}}/>
              </button>
            ))}
          </div>
          <div className="mt-3 text-xs text-[color:var(--text-muted)]">{stickerShape.charAt(0).toUpperCase() + stickerShape.slice(1)} sticker · Kiss-cut vinyl</div>
          <div className="mt-4 pt-4 border-t hairline text-[10px] text-[color:var(--text-muted)] leading-relaxed">
            Full-color print on durable, weatherproof vinyl. Dashed outline on the canvas shows the kiss-cut line — anything you place inside will print; the backing stays rectangular.
          </div>
        </div>
      ) : (
        <div className="bg-white p-5 rounded-lg border hairline">
          <div className="uppercase text-[10px] tracking-[0.3em] font-bold mb-3" style={{color: "var(--secondary)"}}>Material</div>
          <div className="grid grid-cols-2 gap-3">
            {MATERIALS.map((m) => (
              <button key={m.key} data-testid={`material-${m.key}`} onClick={() => setMaterial(m)}
                className={`aspect-[4/3] rounded-md overflow-hidden border-2 transition shadow-inner ${material.key === m.key ? "border-[color:var(--accent-metal)]" : "border-transparent hover:border-[color:var(--border)]"}`}>
                <div style={{backgroundImage: m.bg, backgroundSize: "cover", backgroundPosition: "center"}} className="w-full h-full" title={m.name}/>
              </button>
            ))}
          </div>
          <div className="mt-3 text-xs text-[color:var(--text-muted)]">{material.name}</div>

          <div className="mt-5 pt-4 border-t hairline">
            <div className="uppercase text-[10px] tracking-[0.3em] font-bold mb-3" style={{color: "var(--secondary)"}}>Plaque Shape</div>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="shape-rectangle" onClick={() => setPlaqueShape("rectangle")}
                className={`py-2 rounded-md border-2 text-xs transition ${plaqueShape === "rectangle" ? "border-[color:var(--accent-metal)] bg-[color:var(--accent-metal)]/5" : "border-transparent bg-[color:var(--muted)] hover:border-[color:var(--border)]"}`}>
                Rectangle
              </button>
              <button data-testid="shape-circle" onClick={() => setPlaqueShape("circle")}
                className={`py-2 rounded-md border-2 text-xs transition ${plaqueShape === "circle" ? "border-[color:var(--accent-metal)] bg-[color:var(--accent-metal)]/5" : "border-transparent bg-[color:var(--muted)] hover:border-[color:var(--border)]"}`}>
                Circle
              </button>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t hairline">
            <div className="uppercase text-[10px] tracking-[0.3em] font-bold mb-3" style={{color: "var(--secondary)"}}>Quick Templates</div>
            <Button data-testid="template-baby-plaque" onClick={applyBabyPlaquePreset} variant="outline" size="sm" className="w-full rounded-full">
              Baby Plaque (Circular)
            </Button>
            <div className="mt-2 text-[10px] text-[color:var(--text-muted)] leading-relaxed">Preset a round cherry-wood plaque with baby name + "BORN WITH LOVE". Edit any layer to make it yours.</div>
          </div>
        </div>
      )}

      <div className="bg-white p-5 rounded-lg border hairline">
        <div className="uppercase text-[10px] tracking-[0.3em] font-bold mb-3" style={{color: "var(--secondary)"}}>Layers</div>
        <div className="flex gap-2 mb-3">
          <Button data-testid="add-text-btn" onClick={addText} variant="outline" size="sm" className="flex-1 rounded-full"><Type size={14} className="mr-1"/> Text</Button>
          <label className="flex-1">
            <Button data-testid="add-image-btn" type="button" variant="outline" size="sm" className="w-full rounded-full" onClick={() => document.getElementById('studio-file-input').click()}>
              <Upload size={14} className="mr-1"/> Photo
            </Button>
            <input id="studio-file-input" data-testid="upload-file-input" type="file" accept="image/*" className="hidden" onChange={onUpload}/>
          </label>
        </div>
        <p className="text-[10px] text-[color:var(--text-muted)] leading-relaxed mb-3">Upload any photo — including your pet — and it will preview as a burned-in laser engraving on the material.</p>
        <div className="space-y-1 max-h-56 overflow-auto">
          {layers.map((l) => (
            <div key={l.id} onClick={(e) => selectLayer(l.id, e.shiftKey)}
              data-testid={`layer-${l.id}`}
              className={`flex justify-between items-center px-3 py-2 rounded-md text-xs cursor-pointer ${selectedIds.has(l.id) ? "bg-[color:var(--muted)] ring-1 ring-[color:var(--accent-metal)]/40" : "hover:bg-[color:var(--muted)]/60"}`}>
              <span className="truncate">{l.type === "text" ? `T · ${l.text}` : "🖼 Image"}</span>
              <button onClick={(e) => { e.stopPropagation(); removeLayer(l.id); }}><Trash2 size={13} className="text-[color:var(--text-muted)]"/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
